import { useEffect, useState } from "react";
import { X, Loader2, Trash2, Clock, AlertTriangle, MapPin, Info } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * DayEventsReviewDrawer — Iter 372.p6
 *
 * Right-side drawer showing every raw attendance event for a
 * (user, date) pair. Users clicked the amber '!' or blue '⟳' badge
 * on a register row to open this and diagnose why HOURS look wrong.
 * HR can delete stray events (with an audit-trail reason); the
 * response carries a recomputed row that the caller splices back.
 *
 * Props:
 *   row: the register row that was clicked. { date, user_id,
 *        user_name, in_at, out_at, worked_min, has_multiple_out,
 *        has_dangling_break, ... }
 *   onClose(): dismiss.
 *   onRowsRecomputed(recomputed_rows[]): the caller applies the
 *        splice into its state so the register updates in place.
 */

const KIND_META = {
  in:            { label: "Clock in",       cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  out:           { label: "Clock out",      cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
  break_start:   { label: "Break start",    cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  break_end:     { label: "Break end",      cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-ZA", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

export default function DayEventsReviewDrawer({ row, onClose, onRowsRecomputed }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirmReason, setConfirmReason] = useState(null); // {eid, kind, at}

  useEffect(() => {
    if (!row) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/team/attendance/day-events", {
          params: { user_id: row.user_id, date: row.date },
        });
        if (alive) setItems(data.items || []);
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Could not load events.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [row]);

  const del = async () => {
    if (!confirmReason?.eid) return;
    const reason = (confirmReason.reason || "").trim();
    if (reason.length < 3) {
      toast.error("Reason must be ≥ 3 chars.");
      return;
    }
    setBusy(confirmReason.eid);
    try {
      const { data } = await api.delete(
        `/team/attendance/events/${confirmReason.eid}`,
        { params: { reason } },
      );
      toast.success("Event deleted");
      setItems((prev) => prev.filter((e) => e.id !== confirmReason.eid));
      setConfirmReason(null);
      if (data?.recomputed_rows) onRowsRecomputed?.(data.recomputed_rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(null);
    }
  };

  if (!row) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-start justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="day-events-review-drawer"
    >
      <div
        className="hv-card w-full max-w-lg h-full overflow-y-auto p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-white/10 pb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Day events review</div>
            <h3 className="font-display text-lg font-bold text-white mt-1">
              {row.user_name || row.user_id?.slice(0, 8)}
            </h3>
            <div className="text-xs text-[#a0aabf] mt-0.5 font-mono">{row.date}</div>
            <div className="flex flex-wrap gap-2 text-[10px] text-[#647087] mt-1">
              <span>IN {row.in_at ? fmtTime(row.in_at).split(",")[1]?.trim() : "—"}</span>
              <span>OUT {row.out_at ? fmtTime(row.out_at).split(",")[1]?.trim() : "—"}</span>
              <span>Hours {row.worked_min ? `${Math.floor(row.worked_min / 60)}h${String(row.worked_min % 60).padStart(2, "0")}` : "0h"}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#647087] hover:text-white"
            data-testid="day-events-review-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {(row.has_multiple_out || row.has_dangling_break) && (
          <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-200 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div className="space-y-1">
              {row.has_multiple_out && (
                <div>
                  Multiple OUT punches detected. The register uses envelope math
                  (first IN → last OUT); intermediate stray OUTs are absorbed.
                  Delete any stale OUT below to see the recomputed numbers.
                </div>
              )}
              {row.has_dangling_break && (
                <div>
                  A break was opened but never closed. Break time is ignored so
                  the day accounts correctly. Close the loop by deleting the
                  stray break_start or adding a break_end.
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#647087]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[#647087] text-sm">
            No events on this day.
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map((e) => {
              const meta = KIND_META[e.kind] || { label: e.kind, cls: "bg-white/5 text-white/60 border-white/10" };
              const overridden = !!e.override_reason;
              return (
                <div
                  key={e.id}
                  className="rounded border border-white/10 bg-white/[0.02] p-2 space-y-1"
                  data-testid={`day-event-${e.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.cls}`}>
                        {meta.label}
                      </span>
                      {overridden && (
                        <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
                          OVR
                        </span>
                      )}
                      <span className="font-mono text-xs text-white/90 truncate">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {fmtTime(e.at)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConfirmReason({ eid: e.id, kind: e.kind, at: e.at, reason: "" })}
                      disabled={!!busy}
                      className="text-[#647087] hover:text-rose-300 shrink-0 disabled:opacity-30"
                      title="Delete this event"
                      data-testid={`day-event-delete-${e.id}`}
                    >
                      {busy === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  {(e.lat != null || e.lng != null || e.note || e.override_reason) && (
                    <div className="text-[10px] text-[#647087] space-y-0.5 pl-1">
                      {(e.lat != null && e.lng != null) && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {Number(e.lat).toFixed(5)}, {Number(e.lng).toFixed(5)}
                        </div>
                      )}
                      {e.note && <div><Info className="w-3 h-3 inline mr-1" />{e.note}</div>}
                      {e.override_reason && (
                        <div className="italic">
                          Overridden by {e.overridden_by_name || "HR"}: {e.override_reason}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {confirmReason && (
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setConfirmReason(null)}
          >
            <div
              className="hv-card w-full max-w-sm p-4 space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="font-display text-sm font-bold text-white">
                Delete {KIND_META[confirmReason.kind]?.label || confirmReason.kind} event?
              </h4>
              <div className="text-xs text-[#a0aabf] font-mono">{fmtTime(confirmReason.at)}</div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
                  Reason (audit trail, ≥ 3 chars)
                </span>
                <input
                  type="text"
                  value={confirmReason.reason || ""}
                  onChange={(e) => setConfirmReason((p) => ({ ...p, reason: e.target.value }))}
                  className="hv-input w-full mt-1 text-xs"
                  placeholder="e.g. Duplicate punch, stray record"
                  autoFocus
                  data-testid="day-event-delete-reason"
                />
              </label>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setConfirmReason(null)}
                  className="hv-btn hv-btn-ghost text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={del}
                  disabled={!!busy || (confirmReason.reason || "").trim().length < 3}
                  className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                  data-testid="day-event-delete-confirm"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
